import { Inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { ENDPOINTS } from '../../enums/endpoints.enum';
import { UriUtilitiesService } from '../uri-utilities/uri-utilities.service';
import { UserGidService } from '../user-gid/user-gid.service';
import { HttpClient } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { EncryptionService } from '@evorait/pace-encryption';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { ConcurrentStorageData } from '../../types/concurrent-usage';
import { GWRequestType, GWResponseType } from './concurrent-usage.type';
import { EncodingUtilities } from '../../utilities/encoding-utilities';

@Injectable({ providedIn: 'root' })
export class ConcurrentUsageService {
    private url: string;
    private multiPostUrl: string;
    public constructor(
        private readonly http: HttpClient,
        private readonly uriUtilities: UriUtilitiesService,
        private readonly userGidService: UserGidService,
        private readonly encryptionService: EncryptionService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {
        // URL for posting multiple key value pairs
        this.multiPostUrl = this.uriUtilities.getUriForContent() + ENDPOINTS.UserProfileParentSet;
        // URL for posting single key value pair or GETTING values from key value store
        this.url = this.uriUtilities.getUriForContent() + ENDPOINTS.UserProfileSet;
    }

    public readAllStore(): Observable<ConcurrentStorageData[]> {
        // TODO: this function will need to talk to multiple different indexeddb services and LocalStorage. Concept still to come in SHP-7588.
        return this.http
            .get<GWResponseType>(this.url + '?$format=json')
            .pipe(
                map((res) =>
                    res.d.results
                        .map((entry) =>
                            JSON.parse(
                                this.encryptionService.decrypt(EncodingUtilities.base64DecodeUnicode(entry.value)),
                            ),
                        )
                        .filter((entry) => entry.value && entry.value.length > 0),
                ),
            );
    }

    public writeMultipleEntries(): Observable<void> {
        // TODO: bulk write entries to the store.  Concept still to come in SHP-7588.
        throw new Error('Method not implemented');
    }

    public writeDataForKey(
        key: string,
        value: string,
        timestamp: number = Date.now(),
    ): Observable<ConcurrentStorageData> {
        const concurrencyData: ConcurrentStorageData = { key, value, timestamp };
        const body: GWRequestType = {
            key: this.generateKeyForUser(key),
            value: EncodingUtilities.base64EncodeUnicode(JSON.stringify(concurrencyData)),
            gid: this.userGidService.getUserGid(),
        };

        return this.http.post<void>(this.url, body).pipe(map(() => concurrencyData));
    }

    public readDataForKey(key: string): Observable<ConcurrentStorageData[]> {
        return this.http
            .get<GWResponseType>(this.url + `?$filter=key eq '${this.generateKeyForUser(key)}'&$format=json`)
            .pipe(
                map((res) => {
                    return res.d.results
                        .filter((entry) => entry.value && entry.value.length > 0)
                        .map((entry) =>
                            JSON.parse(
                                // Decrypt is able to handle encrypted and unencrypted data. Leaving decrypt here to handle old data that was encrypted.
                                this.encryptionService.decrypt(EncodingUtilities.base64DecodeUnicode(entry.value)),
                            ),
                        );
                }),
                catchError((error) => {
                    const errorMessage = `[ConcurrencyStore](readDataForKey): Confirmation with following key could not be read from database: ${key}. Error ${error}`;
                    this.log.error(errorMessage);
                    return throwError(() => new Error(errorMessage));
                }),
            );
    }

    public deleteDataForKey(key: string): Observable<void> {
        // TODO: Gateway is lacking the delete feature
        // For now we are filtering the returned values for non-empty entries
        const body: GWRequestType = {
            key: this.generateKeyForUser(key),
            value: '',
            gid: this.userGidService.getUserGid(),
        };

        return this.http.post<void>(this.url, body).pipe(map(() => undefined));
    }

    private generateKeyForUser(key: string): string {
        return `${this.userGidService.getUserGid()}-${key}`;
    }
}
